import { Loading } from '@/components/Loading';
import { useAuth } from '@/context/AuthContext';
import useScreenSize from '@/hooks/use-screen-size';
import useNewNote from '@/hooks/useNewNote';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { cn } from '@/utils/utils';
import { BookOpen } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { parseAsBoolean, parseAsString } from 'nuqs';
import { useQueryState } from 'nuqs';
import { useEffect, useState } from 'react';
import AppLandingForm from './AppLandingForm';
import ButtonCard from './ButtonCard';
import { CreateButton } from './buttons/create';
import { UploadFileButton } from './buttons/upload-file';
import { UrlImportButton } from './buttons/url-import';

const AppLanding = () => {
  const { user } = useAuth();
  const router = useRouter();

  const { track } = useAnalytics();

  const { height } = useScreenSize();

  const [newNoteLoading, setNewNoteLoading] = useState(false);

  const { createNote } = useNewNote({
    onSuccess: (data) => {
      if (!data) return;
      router.push(`/b/${data.id}`);
      track('newCreateNoteClicked');
    },
    onError: (_) => {
      setNewNoteLoading(false);
    },
  });

  const [newNote, setNewNote] = useQueryState('newNote', parseAsBoolean);
  const [referred, setReferred] = useQueryState('referred', parseAsString);
  const [settingMembers, setSettingMembers] = useQueryState(
    'settingMembers',
    parseAsBoolean,
  );
  const [settingRewards, setSettingRewards] = useQueryState(
    'settingRewards',
    parseAsBoolean,
  );
  const userType = user?.onboardingResponse?.length
    ? user.onboardingResponse[user.onboardingResponse.length - 1].purpose
    : 'unknown';

  useEffect(() => {
    if (referred && user) {
      track('email_CTA_click', {
        email: referred,
        user_type: userType,
      });
      setReferred(null);
      if (newNote) {
        setNewNoteLoading(true);
        createNote();
        setNewNote(null);
      }
      if (settingMembers) {
        window.location.href = '/settings/members';
        setSettingMembers(null);
      }
      if (settingRewards && user) {
        window.location.href = '/settings/referrals';
        setSettingRewards(null);
      }
    }
  }, [newNote, referred, user, userType, settingMembers, settingRewards]);

  if (newNoteLoading) return <Loading />;

  return (
    <div className="max-w-[44rem] w-full p-8 flex flex-col gap-4 ">
      <div
        className={cn(
          'ml-4  flex-col hidden md:flex',
          height < 750 && 'max-md:pt-24',
        )}
      >
        <h1 className="text-2xl !tracking-[-0.5px] font-semibold ">
          Welcome {user?.displayName || 'to Anara'}
        </h1>
        <h1 className="text-2xl !tracking-[-0.5px] font-semibold  text-textGray4">
          What will you discover today?
        </h1>
      </div>
      <div className="flex flex-col gap-4">
        <AppLandingForm />
        <div className="grid md:grid-cols-4 grid-cols-1 gap-4">
          <UploadFileButton />
          <UrlImportButton />
          {/* <div className="relative w-full">
            {isDowntime && (
              <DowntimeAlert className="absolute md:-top-7 -bottom-4 md:whitespace-nowrap">
                Import unavailable. We’ll be back online soon!
              </DowntimeAlert>
            )}
          </div> */}
          <CreateButton
            setNewNoteLoading={(loading) => setNewNoteLoading(loading)}
          />
          <Link
            href="/docs"
            target="_blank"
            onClick={() => {
              track('landing_button_clicked', {
                button: 'learn_anara',
              });
            }}
          >
            <ButtonCard label="Learn" icon={BookOpen} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AppLanding;
